import {
    Badge,
    BlockStack,
    Box,
    Card,
    DataTable,
    InlineStack,
    Typography,
    type DataTableColumn,
} from '@pv-frontend/ui-components-web';
import { MeasurementPendingBadge } from './measurement-pending-notice';

/** A weighted arm of a defined experiment. */
export interface ExperimentVariantView {
    name: string;
    weight: number;
}

/** One vault frozen into an experiment, with the arm it drew. */
export interface ExperimentAssignmentView {
    vaultId: string;
    variant: string;
    assignedUtc: string;
    /** When the vault first hit the flag. Always null today — no exposure consumer. */
    firstExposedUtc: string | null;
}

export interface ExperimentView {
    key: string;
    flagKey: string;
    variants: readonly ExperimentVariantView[];
    assignments: readonly ExperimentAssignmentView[];
}

/** One variant row with its weight, computed split, and enrolled cohort size. */
interface VariantRow {
    name: string;
    weight: number;
    percent: number;
    enrolled: number;
}

function variantRows(experiment: ExperimentView): VariantRow[] {
    const total = experiment.variants.reduce((sum, v) => sum + v.weight, 0);
    const enrolledByVariant = new Map<string, number>();
    for (const assignment of experiment.assignments) {
        enrolledByVariant.set(assignment.variant, (enrolledByVariant.get(assignment.variant) ?? 0) + 1);
    }
    return experiment.variants.map((variant) => ({
        name: variant.name,
        weight: variant.weight,
        percent: total === 0 ? 0 : Math.round((variant.weight / total) * 100),
        enrolled: enrolledByVariant.get(variant.name) ?? 0,
    }));
}

const variantColumns: DataTableColumn<VariantRow>[] = [
    {
        key: 'name',
        header: 'Variant',
        render: (row) => <Typography variant="body-sm" fontWeight="medium">{row.name}</Typography>,
    },
    {
        key: 'weight',
        header: 'Weight',
        render: (row) => <Typography variant="body-sm">{row.weight}</Typography>,
    },
    {
        key: 'split',
        header: 'Split',
        render: (row) => <Typography variant="body-sm">{row.percent}%</Typography>,
    },
    {
        key: 'enrolled',
        header: 'Enrolled',
        align: 'end',
        render: (row) => <Typography variant="body-sm">{row.enrolled}</Typography>,
    },
];

function ExperimentCard({ experiment }: { experiment: ExperimentView }) {
    const rows = variantRows(experiment);
    const enrolled = experiment.assignments.length;
    const exposed = experiment.assignments.filter((a) => a.firstExposedUtc != null).length;
    const vaultNoun = enrolled === 1 ? 'vault' : 'vaults';

    return (
        <Card testID={`experiment-${experiment.key}`}>
            <Box padding="400">
                <BlockStack gap="300">
                    <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                        <BlockStack gap="050">
                            <Typography variant="heading-sm" as="h3">
                                {experiment.key}
                            </Typography>
                            <InlineStack gap="150" blockAlign="center">
                                <Typography variant="body-sm" tone="text-secondary">
                                    Flag
                                </Typography>
                                <Badge tone="neutral">{experiment.flagKey}</Badge>
                            </InlineStack>
                        </BlockStack>
                        <MeasurementPendingBadge />
                    </InlineStack>

                    <DataTable
                        columns={variantColumns}
                        rows={rows}
                        keyExtractor={(row) => row.name}
                        emptyState="This experiment has no variants."
                        testID={`variants-${experiment.key}`}
                    />

                    <Typography variant="body-sm" tone="text-secondary" testID={`cohort-${experiment.key}`}>
                        {enrolled === 0
                            ? 'No vaults enrolled yet.'
                            : `${enrolled} ${vaultNoun} enrolled · ${exposed} exposed — measurement pending.`}
                    </Typography>
                </BlockStack>
            </Box>
        </Card>
    );
}

export interface ExperimentListProps {
    experiments: readonly ExperimentView[];
}

/** The defined-experiments list: each card shows its arms, cohort size and the measurement-pending caveat. */
export function ExperimentList({ experiments }: ExperimentListProps) {
    if (experiments.length === 0) {
        return (
            <Card>
                <Box padding="400">
                    <Typography tone="text-secondary">No experiments are defined yet.</Typography>
                </Box>
            </Card>
        );
    }

    return (
        <BlockStack gap="300">
            {experiments.map((experiment) => (
                <ExperimentCard key={experiment.key} experiment={experiment} />
            ))}
        </BlockStack>
    );
}
